import { useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpen, Check, Search } from "lucide-react";
import { observer } from "mobx-react-lite";

import { useTran } from "@/hooks";
import { showToast } from "@/lib/utils/toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import AppDialog from "@/components/common/AppDialog";
import { InputField, SelectField } from "@/components/common";

import QuestionOverridesForm from "./QuestionOverridesForm";
import { useQuestionLibraryStore } from "../hooks";

const buildQuestionPayload = (question, overrides = {}) => ({
  questionBankId: question._id,
  questionText: overrides.questionText || question.questionText,
  description:
    overrides.description !== undefined
      ? overrides.description
      : question.description,
  category: overrides.category || question.category,
  questionType: overrides.questionType || question.questionType,
  options: overrides.options || question.options,
  points: overrides.points !== undefined ? overrides.points : question.points,
  required:
    overrides.required !== undefined ? overrides.required : question.required,
  placeholder:
    overrides.placeholder !== undefined
      ? overrides.placeholder
      : question.placeholder,
  minValue:
    overrides.minValue !== undefined ? overrides.minValue : question.minValue,
  maxValue:
    overrides.maxValue !== undefined ? overrides.maxValue : question.maxValue,
  allowOther:
    overrides.allowOther !== undefined
      ? overrides.allowOther
      : question.allowOther,
  metadata: overrides.metadata || question.metadata,
  sampleAnswer:
    overrides.sampleAnswer !== undefined
      ? overrides.sampleAnswer
      : question.sampleAnswer,
  originType: "standard",
  originQuestionId: question.parentQuestionId || question._id,
  overrides: Object.keys(overrides).length > 0 ? overrides : undefined,
});

const QuestionLibrary = observer(
  ({
    open,
    onClose,
    onSelect,
    onRemove,
    categories = [],
    selectedQuestionIds = [],
    standardSurveys = [],
  }) => {
    const { t } = useTran();
    const libraryStore = useQuestionLibraryStore();

    const alreadyAddedSet = useMemo(
      () => new Set(selectedQuestionIds),
      [selectedQuestionIds],
    );
    const selectedQuestionIdsSet = useMemo(
      () => new Set(libraryStore.selectedQuestionIds),
      [libraryStore.selectedQuestionIds],
    );
    const categoryOptions = useMemo(
      () => [
        { key: "all", label: t("All Categories") },
        ...categories.map((cat) => ({
          key: cat,
          label: cat.charAt(0).toUpperCase() + cat.slice(1),
        })),
      ],
      [categories, t],
    );

    useEffect(() => {
      if (open) {
        libraryStore.initialize();
      } else {
        libraryStore.resetState();
      }
    }, [libraryStore, open]);

    const handleSelectQuestion = (question) => {
      libraryStore.openOverrides(question);
    };

    const handleAddWithOverrides = (overrides = {}) => {
      if (!libraryStore.selectedQuestion) return;
      const questionToAdd = buildQuestionPayload(
        libraryStore.selectedQuestion,
        overrides,
      );
      setTimeout(() => {
        onSelect?.(questionToAdd);
        libraryStore.closeOverrides();
      }, 150);
    };

    const handleAddDirectly = (question) => {
      if (!question || alreadyAddedSet.has(question._id)) return;
      libraryStore.toggleSelectedQuestion(question);
    };

    const handleRemoveQuestion = (questionId) => {
      onRemove?.(questionId);
      libraryStore.removeSelectedQuestion(questionId);
    };

    const handleAddSelected = () => {
      const toAdd = libraryStore.selectedQuestions.filter(
        (question) => question && !alreadyAddedSet.has(question._id),
      );
      toAdd.forEach((question, index) => {
        setTimeout(
          () => onSelect?.(buildQuestionPayload(question)),
          index * 25,
        );
      });
      libraryStore.clearSelectedQuestions();
      if (toAdd.length) {
        showToast.success(
          t("Added {{count}} questions.", { count: toAdd.length }),
        );
        onClose?.();
      }
    };

    return (
      <AppDialog
        open={open}
        onOpenChange={onClose}
        title={t("Question Library")}
        description={t(
          "Browse published standard templates and add questions to your survey.",
        )}
        size="2xl"
        bodyClassName="p-0"
        contentClassName="max-w-6xl"
      >
        {libraryStore.initializing ? (
          <div className="flex min-h-[320px] items-center justify-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent"
            />
          </div>
        ) : (
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 lg:grid-cols-[280px_1fr]">
            <div className="flex min-h-0 flex-col border-r border-border bg-muted/40 p-4  ">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-semibold uppercase tracking-wide text-primary">
                  {t("Standard templates")}
                </p>
                <Badge className="bg-primary text-primary-foreground ring-1 ring-primary/80">
                  {standardSurveys.length}
                </Badge>
              </div>
              <div className="max-h-[70vh] space-y-2 overflow-y-auto pr-1">
                {libraryStore.standardSurveys.length === 0 ? (
                  <div className="rounded-radius border border-dashed border-border bg-card p-4 text-center text-sm text-muted-foreground">
                    {t("No published standard surveys available.")}
                  </div>
                ) : (
                  libraryStore.standardSurveys.map((survey) => {
                    const active = survey._id === libraryStore.selectedSurveyId;
                    const questionTotal =
                      typeof survey.questionCount === "number"
                        ? survey.questionCount
                        : Array.isArray(survey.questions)
                          ? survey.questions.length
                          : 0;
                    return (
                      <button
                        key={survey._id}
                        type="button"
                        onClick={() =>
                          libraryStore.setSelectedSurveyId(survey._id)
                        }
                        className={`w-full rounded-radius border p-3 text-left transition ${
                          active
                            ? "border-primary bg-primary/10 ring-2 ring-primary/40 shadow-sm"
                            : "border-border bg-card hover:border-primary/50 hover:shadow  "
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="line-clamp-2 text-sm font-semibold text-foreground">
                            {survey.title}
                          </p>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-card text-foreground ring-1 ring-border">
                              {questionTotal}
                            </Badge>
                          </div>
                        </div>
                        {survey.description ? (
                          <p className="mt-1 line-clamp-3 text-xs text-muted-foreground">
                            {survey.description}
                          </p>
                        ) : null}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="flex min-h-0 flex-col">
              <div className="border-b border-border bg-card px-5 py-4  ">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex flex-1 items-center gap-3">
                    <InputField
                      value={libraryStore.search}
                      onChange={(event) =>
                        libraryStore.setSearch(event.target.value)
                      }
                      placeholder={t("Search questions...")}
                      leftIcon={
                        <Search className="h-4 w-4 text-muted-foreground" />
                      }
                      className="w-full space-y-0"
                    />
                    <SelectField
                      value={libraryStore.categoryFilter}
                      onValueChange={libraryStore.setCategoryFilter}
                      options={categoryOptions}
                      placeholder={t("All Categories")}
                      className="w-44 space-y-0"
                    />
                  </div>
                  <Button
                    size="sm"
                    className="rounded-full"
                    onClick={handleAddSelected}
                    disabled={libraryStore.selectedQuestions.length === 0}
                  >
                    {t("Add Selected")} ({libraryStore.selectedQuestions.length}
                    )
                  </Button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5">
                <AnimatePresence mode="wait">
                  {libraryStore.loadingQuestions ? (
                    <motion.div
                      key="loading"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex h-64 flex-col items-center justify-center text-sm text-muted-foreground"
                    >
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                        className="mb-3 h-6 w-6 rounded-full border-2 border-primary border-t-transparent"
                      />
                      {t("Loading questions...")}
                    </motion.div>
                  ) : libraryStore.filteredQuestions.length === 0 ? (
                    <motion.div
                      key="empty"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex h-64 flex-col items-center justify-center text-sm text-muted-foreground"
                    >
                      <BookOpen className="mb-3 h-10 w-10 text-muted-foreground" />
                      {t("No questions found")}
                    </motion.div>
                  ) : (
                    <motion.div
                      key="questions"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="grid gap-4 md:grid-cols-2"
                    >
                      {libraryStore.filteredQuestions.map((question, index) => {
                        const isSelected = selectedQuestionIdsSet.has(
                          question._id,
                        );
                        const isAdded = alreadyAddedSet.has(question._id);
                        return (
                          <motion.div
                            key={question._id}
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                              !isAdded
                                ? libraryStore.toggleSelectedQuestion(question)
                                : null
                            }
                            onKeyDown={(event) => {
                              if (isAdded) return;
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                libraryStore.toggleSelectedQuestion(question);
                              }
                            }}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.02 }}
                            className={`relative flex h-full flex-col rounded-radius border p-4 text-left shadow-sm ring-1 ring-transparent transition hover:-translate-y-[1px] ${
                              isSelected
                                ? "border-primary/70 bg-primary/10 ring-primary/30"
                                : isAdded
                                  ? "border-border bg-muted/40 text-muted-foreground  "
                                  : "border-border bg-card hover:border-primary/40 hover:ring-primary/15"
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="space-y-1">
                                <p className="text-xs font-semibold text-foreground">
                                  {question.questionText}
                                </p>
                                {question.description ? (
                                  <p className="line-clamp-2 text-[11px] text-muted-foreground">
                                    {question.description}
                                  </p>
                                ) : null}
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                <Badge
                                  variant="secondary"
                                  className="bg-muted/60 text-[11px] text-foreground  "
                                >
                                  {question.category || "common"}
                                </Badge>
                              </div>
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                              <Badge className="bg-primary/10 text-[11px] text-primary">
                                {question.questionType?.replace("_", " ") ||
                                  "text"}
                              </Badge>
                              <span
                                className={`flex items-center gap-1 rounded-full px-2 py-0.5 ${
                                  isSelected
                                    ? "bg-primary/10 text-primary ring-1 ring-primary/30"
                                    : "bg-muted/40 text-muted-foreground ring-1 ring-border"
                                }`}
                              >
                                <Check className="h-3 w-3" />
                                {t("Selected")}
                              </span>
                            </div>

                            <div className="mt-4 flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={isAdded}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    if (!isAdded)
                                      handleSelectQuestion(question);
                                  }}
                                >
                                  {t("Customize")}
                                </Button>
                                <Button
                                  size="sm"
                                  variant={isAdded ? "outline" : "default"}
                                  disabled={isAdded}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    if (!isAdded) handleAddDirectly(question);
                                  }}
                                >
                                  {isAdded ? t("Added") : t("Add")}
                                </Button>
                              </div>
                              {isAdded ? (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleRemoveQuestion(question._id);
                                  }}
                                >
                                  {t("Remove")}
                                </Button>
                              ) : (
                                <Button
                                  size="icon"
                                  variant={isSelected ? "default" : "outline"}
                                  className={`h-8 w-8 rounded-full ${
                                    isSelected
                                      ? "text-primary-foreground"
                                      : "text-muted-foreground"
                                  }`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    libraryStore.toggleSelectedQuestion(
                                      question,
                                    );
                                  }}
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                            {isAdded ? (
                              <p className="mt-2 text-[11px] text-primary">
                                {t("Already added")}
                              </p>
                            ) : null}
                          </motion.div>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        )}

        <AnimatePresence>
          {libraryStore.showOverrides ? (
            <AppDialog
              open={libraryStore.showOverrides}
              onOpenChange={libraryStore.closeOverrides}
              title={t("Customize Question")}
              description={t(
                "Optionally customize this question before adding it to your survey.",
              )}
              size="lg"
              bodyClassName="p-0"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                {libraryStore.selectedQuestion ? (
                  <QuestionOverridesForm
                    question={libraryStore.selectedQuestion}
                    onSave={handleAddWithOverrides}
                    onCancel={() => {
                      libraryStore.closeOverrides();
                    }}
                  />
                ) : null}
              </motion.div>
            </AppDialog>
          ) : null}
        </AnimatePresence>
      </AppDialog>
    );
  },
);

export default QuestionLibrary;
